import Accordion from '@/compoments/common/Accordion'
import UserPart from '@/compoments/common/UserPart'
import GMap from '@/compoments/features/GMap'
import PageLoader from '@/compoments/PageLoader'
import useDebounce from '@/hooks/useDebounce'
import { useGetDispatcherMapQuery } from '@/services/dispatcher/dispather'
import {
    enumPillStatusesClasses,
    enumPillStatusesText,
    JobStatusesType,
} from '@/types/tenant/workGroup.types'
import { getMediaUrl } from '@/utils/helpers'
import clsx from 'clsx'
import { format } from 'date-fns'
import { useState } from 'react'

interface IRenderPopupContentProps {
    title: string
    type?: string
    note?: string
    startTime?: string
    endTime?: string
    status?: JobStatusesType
    service?: {
        name?: string
        description?: string
        price?: number
    }
    customer?: {
        fullName?: string
        phone?: string
        email?: string
        photo?: string
    }
    technician?: {
        fullName?: string
        phone?: string
        email?: string
        photo?: string
    }
}

const renderPopupContent = ({
    title,
    technician,
    customer,
    note,
    startTime,
    endTime,
    status,
    service,
    type,
}: IRenderPopupContentProps) => {
    return (
        <div className="map-custom-modal">
            <div className="map-custom-body">
                <div className="map-custom-heading">
                    <h4 className="map-custom-title">{title}</h4>
                    {status && (
                        <span
                            className={clsx(
                                'pill pill-2',
                                enumPillStatusesClasses[status],
                            )}
                        >
                            {enumPillStatusesText[status] || status}
                        </span>
                    )}
                </div>
                {customer && (
                    <div className="map-custom-note">
                        <h5>Customer</h5>
                        {customer.phone && (
                            <span className="text--12 text-color-1">
                                {customer.phone}
                            </span>
                        )}
                        <UserPart
                            src={customer.photo}
                            name={customer.fullName}
                            email={customer.email}
                        />
                    </div>
                )}
                {service && (
                    <div className="map-custom-price">
                        <b>Price:</b> ${service.price}
                    </div>
                )}
                {startTime && endTime && (
                    <div className="map-custom-order-range">
                        <span>
                            <b>Start:</b>{' '}
                            {format(new Date(startTime), 'MMM dd, HH:mm')}{' '}
                        </span>
                        <span>
                            <b>End:</b>{' '}
                            {format(new Date(endTime), 'MMM dd, HH:mm')}
                        </span>
                    </div>
                )}
                {note && (
                    <div className="map-custom-note">
                        <h5>Note</h5>
                        <p>{note}</p>
                    </div>
                )}
                {service && (
                    <div className="map-custom-note">
                        <h5>Service</h5>
                        <div>
                            <p>
                                <b>Name:</b> {service.name}
                            </p>
                            <p>
                                <b>Description:</b> {service.description}
                            </p>
                        </div>
                    </div>
                )}

                {technician && (
                    <div className="map-custom-note">
                        {type !== 'technician' && <h5>Technician</h5>}
                        {technician.phone && (
                            <span className="text--12 text-color-1">
                                {technician.phone}
                            </span>
                        )}
                        <UserPart
                            src={technician.photo}
                            name={technician.fullName}
                            email={technician.email}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

type MapQueryType = {
    latitude: number
    longitude: number
    radius: number
}

const ResourceMap = () => {
    const [mapQuery, setMapQuery] = useState<MapQueryType>({
        latitude: 37.42382537,
        longitude: -122.08559887,
        radius: 10,
    })
    const { data: response, isLoading } = useGetDispatcherMapQuery({
        query: mapQuery,
    })

    const onBoundsChanged = (bounds: MapQueryType) => {
        setMapQuery(bounds)
    }

    const debouncedOnBoundsChanged = useDebounce(onBoundsChanged, 300)

    const data = response?.data

    if (isLoading) {
        return <PageLoader style={{ height: 250 }} />
    }

    if (!data) return

    const technicians = data.technicians.map(t => ({
        longitude: parseFloat(t.address.longitude),
        latitude: parseFloat(t.address.latitude),
        popupData: {
            title: 'Technician',
            type: 'technician',
            technician: {
                photo: t.photo,
                fullName: t.fullName,
                phone: t.phone,
                email: t.email,
            },
        },
        icon: getMediaUrl(t.photo),
    }))
    const activeOrders = data.activeOrders.map(t => ({
        longitude: parseFloat(t.address.longitude),
        latitude: parseFloat(t.address.latitude),
        popupData: {
            title: 'Order',
            type: 'order',
            note: t.note,
            startTime: t.startTime,
            endTime: t.endTime,
            status: t.status,
            technician: {
                photo: t.technician?.photo,
                fullName: t.technician?.fullName,
                phone: t.technician?.phone,
                email: t.technician?.email,
            },
            service: {
                name: t.service.name,
                description: t.service.description,
                price: t.service.price,
            },
            customer: {
                fullName: t.customer.fullName,
                photo: t.customer.photo,
                phone: t.customer.phone,
                email: t.customer.email,
            },
        },
    }))
    const newOrders = data.newOrders?.rows?.map(t => ({
        longitude: parseFloat(t.address.longitude),
        latitude: parseFloat(t.address.latitude),
        popupData: {
            title: 'New order',
            type: 'order',
            note: t.note,
            startTime: t.startTime,
            endTime: t.endTime,
            status: t.status,
            service: {
                name: t.service.name,
                description: t.service.description,
                price: t.service.price,
            },
            customer: {
                fullName: t.customer.fullName,
                photo: t.customer.photo,
                phone: t.customer.phone,
                email: t.customer.email,
            },
        },
    }))

    const markers = [...technicians, ...activeOrders, ...newOrders]

    return (
        <Accordion
            defaultOpen
            header={<div className="accordeon-info">Map</div>}
        >
            {/* <MapBox markers={markers} renderPopupContent={renderPopupContent} /> */}
            <GMap
                markers={markers}
                renderPopupContent={renderPopupContent}
                onBoundsChanged={debouncedOnBoundsChanged}
            />
        </Accordion>
    )
}

export default ResourceMap
